import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FilePlus2, FileSignature, Loader2, PenLine } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ENGAGEMENTS, ENGAGEMENT_KINDS, type EngagementKind } from '@jt/domain';
import { api } from '../lib/api';
import { HelpHint } from '../components/HelpHint';
import { PageNav } from '../components/PageNav';
import { useToast } from '../components/Toast';
import { useSession } from '../lib/session';
import type { StaffContractRow } from '../lib/types';

/**
 * Contracts of employment: yours, and — for HR — everybody's.
 *
 * ## Why one screen and not two
 *
 * A technician wants one thing here: the contract with their name on it. HR
 * wants the list and the ability to start a new one. Those are different jobs
 * but they are the same object, the same statuses and the same route to the
 * same signing screen, so splitting them would have meant two lists to keep
 * agreeing with each other.
 *
 * "Mine" is therefore always shown, because everybody has one or will, and the
 * roster below it appears only for whoever holds `staff_contract:read_all`.
 * That is a presentational choice; the API refuses the list either way.
 */
export function StaffContractsPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  const canManage = can('staff_contract:manage');
  const canSeeAll = can('staff_contract:read_all');

  const [raising, setRaising] = useState(false);
  const [userId, setUserId] = useState('');
  const [kind, setKind] = useState<EngagementKind>('employment');
  const [startDate, setStartDate] = useState('');

  const mine = useQuery({ queryKey: ['staff-contracts', 'mine'], queryFn: api.myStaffContracts });

  const all = useQuery({
    queryKey: ['staff-contracts', 'all'],
    queryFn: () => api.staffContracts(),
    enabled: canSeeAll,
  });

  /*
   * The roster of people a contract can be raised for.
   *
   * Read from the user list rather than the technician roster because a
   * contract is not a technician's privilege — the accountant and the
   * dispatcher have one too. Only fetched when there is a form to fill.
   */
  const people = useQuery({ queryKey: ['users'], queryFn: api.users, enabled: raising });

  const raise = useMutation({
    mutationFn: () => api.raiseStaffContract({ userId, kind, startDate }),
    onSuccess: async (contract) => {
      toast.done(`${contract.reference} is ready for the company to sign.`);
      setRaising(false);
      setUserId('');
      setStartDate('');
      await queryClient.invalidateQueries({ queryKey: ['staff-contracts'] });
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  if (mine.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const mineRows = mine.data?.contracts ?? [];
  const allRows = all.data?.contracts ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageNav crumbs={[{ label: 'People', to: '/users' }, { label: 'Contracts' }]} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Contracts of employment
            <HelpHint>
              Somebody signs for the business first, and only then can the person it is for fill in
              their own details and sign. Nothing is in force until both have.
            </HelpHint>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Employment runs a year, an attachment three months, an internship six.
          </p>
        </div>

        {canManage && !raising && (
          <button type="button" className="btn-primary" onClick={() => setRaising(true)}>
            <FilePlus2 className="h-4 w-4" aria-hidden="true" />
            Raise a contract
          </button>
        )}
      </div>

      {raising && (
        <section className="card space-y-3">
          <h2 className="font-heading text-lg">Raise a contract</h2>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm">
              For whom
              <select
                className="input mt-1 w-full"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
              >
                <option value="">Choose somebody…</option>
                {(people.data?.users ?? [])
                  .filter((one) => one.isActive)
                  .map((one) => (
                    <option key={one.id} value={one.id}>
                      {one.name} — {one.role}
                    </option>
                  ))}
              </select>
              {people.isPending && (
                <span className="mt-1 block text-xs text-muted-foreground">reading the roster…</span>
              )}
            </label>

            <label className="text-sm">
              Engagement
              <select
                className="input mt-1 w-full"
                value={kind}
                onChange={(event) => setKind(event.target.value as EngagementKind)}
              >
                {ENGAGEMENT_KINDS.map((one) => (
                  <option key={one} value={one}>
                    {ENGAGEMENTS[one].label} — {ENGAGEMENTS[one].months} months
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-muted-foreground">
                {ENGAGEMENTS[kind].note}
              </span>
            </label>

            <label className="text-sm">
              Starting
              <input
                type="date"
                className="input mt-1 w-full"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                The end date is worked out when the company signs.
              </span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={!userId || !startDate || raise.isPending}
              onClick={() => raise.mutate()}
            >
              {raise.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Raise it
            </button>
            <button type="button" className="btn-secondary" onClick={() => setRaising(false)}>
              Cancel
            </button>
            <span className="text-xs text-muted-foreground">
              It goes to whoever signs for the business, not to the employee.
            </span>
          </div>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="font-heading text-lg">Mine</h2>
        {mineRows.length === 0 ? (
          <p className="card text-sm text-muted-foreground">
            You have no contract on the system yet. HR raises it; you will be asked to sign once
            the business has.
          </p>
        ) : (
          <ul className="space-y-2">
            {mineRows.map((row) => (
              <ContractLine key={row.id} row={row} yours />
            ))}
          </ul>
        )}
      </section>

      {canSeeAll && (
        <section className="space-y-2">
          <h2 className="font-heading text-lg">Everybody’s</h2>
          {all.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
          ) : allRows.length === 0 ? (
            <p className="card text-sm text-muted-foreground">
              Nobody has a contract on the system yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {allRows.map((row) => (
                <ContractLine key={row.id} row={row} />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

/**
 * One contract, as a row.
 *
 * The row leads with whose turn it is rather than the status, because that is
 * the only part anybody acts on. `AWAITING_COMPANY` and `AWAITING_EMPLOYEE`
 * are the same fact said twice; "waiting for you" is the useful reading of it.
 */
function ContractLine({ row, yours = false }: { row: StaffContractRow; yours?: boolean }) {
  const waiting = row.next?.label ?? null;
  const yourTurn = yours && row.turn === 'counterparty';

  const badge =
    row.status === 'SIGNED'
      ? { text: 'signed', tone: 'bg-secondary-100 text-secondary-800' }
      : row.status === 'VOIDED'
        ? { text: 'cancelled', tone: 'bg-muted text-muted-foreground' }
        : row.status === 'SUPERSEDED'
          ? { text: 'replaced', tone: 'bg-muted text-muted-foreground' }
          : yourTurn
            ? { text: 'needs your signature', tone: 'bg-accent-100 text-accent-900' }
            : { text: `waiting for ${waiting ?? 'a signature'}`, tone: 'bg-primary-100 text-primary-800' };

  const date = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';

  return (
    <li>
      <Link
        to={`/staff-contracts/${row.id}`}
        className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-border p-3 hover:bg-muted/40"
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {yourTurn ? (
            <PenLine className="h-4 w-4 shrink-0 text-accent-700" aria-hidden="true" />
          ) : (
            <FileSignature className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{row.holder.name}</span>
            <span className="block text-xs text-muted-foreground">
              {row.reference} · {row.title}
            </span>
          </span>
        </span>

        <span className="text-xs text-muted-foreground">
          {date(row.startDate)} → {row.endDate ? date(row.endDate) : 'end set on signing'}
        </span>

        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.tone}`}>
          {badge.text}
        </span>
      </Link>
    </li>
  );
}
